import React, { useEffect, useState } from 'react'
import { WithdrawalDetailPresenter } from './withdrawal-detail-presenter'
import {
  InvestorBankDetails,
  InvestorProps,
  KYCDetailsWithDocument,
  Opportunity,
  WithdrawRequest,
} from '../../lib/types'
import { useParams } from 'react-router-dom'
import {
  investorServices,
  kycServices,
  opportunityServices,
  withdrawalServices,
} from '../../services'
import { showBanner } from '../../global-state/banner-slice'
import { useDispatch, useSelector } from 'react-redux'
import { RootState } from '../../store'
import { isActionAllowed } from '../../lib/permissions/adminActions'
import { getOrderStatusErrorMessage, parseToCents } from '../../lib/utils/helpers'
import { EngagementEvents, logCrmEvent } from '../../lib/utils/crmEvents'

const WithdrawalDetailContainer = () => {
  const dispatch = useDispatch()
  const { actionGrants } = useSelector((s: RootState) => s.gbcUser)
  const allowWithdrawalOrderStatusUpdates = isActionAllowed(
    actionGrants,
    'updateOrderWithdrawalStatus',
  )
  const { projectId, investorId, withdrawalId } = useParams()
  const [opportunity, setOpportunity] = useState<Opportunity>({} as Opportunity)
  const [investorProfile, setInvestorProfile] = useState<InvestorProps>({} as InvestorProps)
  const [kycData, setKycData] = useState<KYCDetailsWithDocument>({} as KYCDetailsWithDocument)
  const [investorBankDetails, setInvestorBankDetails] = useState<InvestorBankDetails>(
    {} as InvestorBankDetails,
  )
  const [withdrawalRequestDetail, setWithdrawalRequestDetail] = useState<WithdrawRequest>(
    {} as WithdrawRequest,
  )

  const getWithdrawalRequestDetails = async () => {
    const response = await withdrawalServices.getWithdrawalRequestDetails(
      String(projectId),
      String(investorId),
    )
    if (response && response.data && response.status === 200) {
      setWithdrawalRequestDetail(response.data)
    }
  }

  // Fetch opportunity information
  useEffect(() => {
    const getOpportunityDetails = async () => {
      const response = await opportunityServices.getOpportunityById(projectId as string, false)
      if (!!response && response.data && response.status === 200) {
        setOpportunity(response.data)
      }
    }

    if (projectId) {
      getOpportunityDetails()
    }
  }, [projectId])

  // Fetch investor information, KYC details and bank details
  useEffect(() => {
    const getInvestorProfileDetails = async () => {
      const response = await investorServices.getInvestorProfile(Number(investorId))
      if (response) {
        setInvestorProfile(response)
      }
    }

    const getKYC = async () => {
      const response = await kycServices.getDocumentWithKYC(String(investorId))
      if (!!response && response.data) {
        setKycData(response.data ?? {})
      }
    }

    const getInvestorBankDetails = async () => {
      const response = await kycServices.getInvestorBankDetails(String(investorId))
      if (!!response && response.data) {
        setInvestorBankDetails(response.data)
      }
    }

    if (investorId) {
      getInvestorProfileDetails()
      getKYC()
      getInvestorBankDetails()
    }
  }, [investorId])

  useEffect(() => {
    if (investorId && projectId) {
      getWithdrawalRequestDetails()
    }
  }, [investorId, projectId])

  const getKYCReportLink = async (investorKycId: string) => {
    const response = await kycServices.generateKYCReportLink(investorKycId)

    if (response.status === 200 && response.data) {
      return response.data
    }
  }

  const handleGenerateKYCReportBtnClick = async (kycId: string) => {
    try {
      const { reportLink } = await getKYCReportLink(kycId)

      logCrmEvent(EngagementEvents.CLICKED_VIEW_KYC_REPORT, {
        page: 'withdrawal-detail',
        withdrawalId: withdrawalRequestDetail?.id,
        investorId: investorProfile?.userId,
      })

      window.open(reportLink)
    } catch (error) {
      console.error(error)
    }
  }

  const handleUpdateStatus = async (payload: Record<string, unknown>) => {
    const response = await withdrawalServices.updateWithdrawalStatus(String(withdrawalId), payload)
    if (response.data && response.status === 200) {
      dispatch(
        showBanner({
          text: response.data && response.data.message,
          variant: 'success-banner',
        }),
      )

      logCrmEvent(EngagementEvents.UPDATED_WITHDRAWAL_STATUS, {
        ...payload,
        withdrawalId,
        investorId,
      })

      await getWithdrawalRequestDetails()
      return { success: true as const }
    }

    dispatch(
      showBanner({
        text: getOrderStatusErrorMessage(
          response,
          'Something went wrong in updating withdrawal status',
        ),
        variant: 'error-banner',
      }),
    )
    return {
      success: false as const,
      errorCode: response?.data?.code as string | undefined,
      errorMessage: getOrderStatusErrorMessage(
        response,
        'Something went wrong in updating withdrawal status',
      ),
    }
  }

  const handleStatus = async ({
    value,
    field,
    description,
    units,
    actualAmountSent,
  }: {
    value: string | number
    field: string
    description: string
    units?: number
    actualAmountSent?: string
  }) => {
    let payload: Record<string, unknown> = { [field]: value }

    if (field === 'withdrawalStatus' && value === 'REJECTED') {
      payload = {
        ...payload,
        metadata: { statusDescription: description },
      }
    }
    if (field === 'withdrawalStatus' && value === 'TRADE_EXECUTED') {
      payload = {
        ...payload,
        ...(units !== undefined ? { units } : {}),
        actualAmountSent: actualAmountSent ? parseToCents(actualAmountSent) : undefined,
      }
    }
    return handleUpdateStatus(payload)
  }

  return (
    <WithdrawalDetailPresenter
      opportunity={opportunity}
      investorProfile={investorProfile}
      kycData={kycData}
      investorBankDetails={investorBankDetails}
      withdrawalRequestDetail={withdrawalRequestDetail}
      allowWithdrawalOrderStatusUpdates={allowWithdrawalOrderStatusUpdates}
      handleGenerateKYCReportBtnClick={handleGenerateKYCReportBtnClick}
      handleStatus={handleStatus}
    />
  )
}

export { WithdrawalDetailContainer }
